
import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Download } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { toast } from 'sonner';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const PWAInstallPrompt = () => {
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const { language } = useLanguage();
  const isMobile = useIsMobile();

  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      // Prevent Chrome 67 and earlier from automatically showing the prompt
      e.preventDefault();
      // Store the event for later use
      setInstallPrompt(e as BeforeInstallPromptEvent);
      // Update UI to show install button
      setIsInstallable(true);
    };

    // Check if the app is already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstallable(false);
    } else {
      window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    }

    // Check for iOS devices which need special handling
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
    
    if (isIOS && isSafari && !window.matchMedia('(display-mode: standalone)').matches) {
      // iOS Safari can't use the standard install prompt, so we just show the button
      // that will display instructions when clicked
      setIsInstallable(true);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    // Check if this is iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    
    if (isIOS) {
      // For iOS, show instructions to add to home screen
      toast.info(
        language === 'ur' 
          ? 'اپنے ہوم اسکرین پر شامل کرنے کے لئے، شیئر بٹن دبائیں اور پھر "ہوم اسکرین میں شامل کریں" منتخب کریں'
          : 'To install, tap the share button and then "Add to Home Screen"',
        {
          duration: 6000,
        }
      );
      return;
    }

    if (!installPrompt) return;
    
    // Show the install prompt
    await installPrompt.prompt();
    
    // Wait for the user to respond to the prompt
    const choiceResult = await installPrompt.userChoice;
    
    if (choiceResult.outcome === 'accepted') {
      toast.success(
        language === 'ur' 
          ? 'ایپ کامیابی سے انسٹال ہو گیا'
          : 'App installed successfully'
      );
    }
    
    // Reset the install prompt variable
    setInstallPrompt(null);
    setIsInstallable(false);
  };

  if (!isInstallable) return null;

  return (
    <Button 
      onClick={handleInstallClick} 
      variant="outline" 
      className={`flex items-center gap-2 bg-blue-100 border-blue-200 hover:bg-blue-200 transition-colors fixed ${isMobile ? 'bottom-20' : 'bottom-16'} ${language === 'ur' ? 'right-4' : 'left-4'} z-40 shadow-lg`}
      size="sm"
    >
      <Download size={16} />
      {language === 'ur' ? 'ایپ انسٹال کریں' : 'Install App'}
    </Button>
  );
};

export default PWAInstallPrompt;
